"use client";
import { motion } from "framer-motion";
import { ShieldCheck, Lightbulb, TableProperties } from "lucide-react";
import Link from "next/link";

export function ToolsSection() {
  return (
    <section className="py-20 md:py-32 bg-background">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header — left-aligned, editorial */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12 md:mb-16"
        >
          <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-4">
            The Toolkit
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.1]">
            Four tools.<br />One platform.
          </h2>
        </motion.div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

          {/* Assignment Scorer — featured, dark, 2 rows tall */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="md:row-span-2"
          >
            <Link
              href="/login?redirect=/scorer"
              className="group flex flex-col justify-between h-full min-h-[280px] rounded-2xl bg-foreground border border-foreground/10 p-7 hover:border-foreground/20 transition-colors duration-200"
            >
              <div>
                <span className="text-[11px] font-mono text-background/40">01</span>
                <h3 className="text-xl font-semibold text-background mt-4 mb-3">
                  Assignment Scorer
                </h3>
                <p className="text-sm text-background/60 leading-relaxed">
                  Upload your work and rubric. Get a grade prediction with per-criterion feedback before you submit — not after.
                </p>
              </div>

              {/* Mini grade mockup */}
              <div className="mt-8">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-11 h-11 rounded-full border border-background/50 flex items-center justify-center text-base font-bold text-background shrink-0">
                    A
                  </div>
                  <div className="flex-1 space-y-2">
                    {[88, 74, 92].map((w, i) => (
                      <div key={i} className="h-1.5 w-full rounded-full bg-background/20">
                        <div
                          className="h-full rounded-full bg-background/50"
                          style={{ width: `${w}%` }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <span className="text-xs font-medium text-background/70 group-hover:text-background transition-colors">
                  Try free →
                </span>
              </div>
            </Link>
          </motion.div>

          {/* AI Detection */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.07 }}
            viewport={{ once: true }}
          >
            <Link
              href="/login?redirect=/ai-detection"
              className="group flex flex-col justify-between h-full min-h-[160px] rounded-2xl border border-border bg-card p-6 hover:border-foreground/30 transition-colors duration-200"
            >
              <div className="flex items-start justify-between">
                <span className="text-[11px] font-mono text-muted-foreground">02</span>
                <ShieldCheck className="w-4 h-4 text-muted-foreground/50" />
              </div>
              <div className="mt-6">
                <h3 className="text-base font-semibold mb-1.5">AI Detection</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  See exactly what your professor's detector will flag — before it matters.
                </p>
                <span className="mt-4 inline-block text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                  Try free →
                </span>
              </div>
            </Link>
          </motion.div>

          {/* Topic Generator */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            viewport={{ once: true }}
          >
            <Link
              href="/login?redirect=/topic-generator"
              className="group flex flex-col justify-between h-full min-h-[160px] rounded-2xl border border-border bg-card p-6 hover:border-foreground/30 transition-colors duration-200"
            >
              <div className="flex items-start justify-between">
                <span className="text-[11px] font-mono text-muted-foreground">03</span>
                <span className="text-[10px] font-mono tracking-wider text-emerald-600 dark:text-emerald-500">
                  NEW
                </span>
              </div>
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-1.5">
                  <h3 className="text-base font-semibold">Topic Generator</h3>
                  <Lightbulb className="w-3.5 h-3.5 text-muted-foreground/40" />
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Stuck choosing a topic? Generate research-worthy ideas tailored to your subject and level.
                </p>
                <span className="mt-4 inline-block text-xs font-medium text-muted-foreground group-hover:text-foreground transition-colors">
                  Try free →
                </span>
              </div>
            </Link>
          </motion.div>

          {/* Survey Filler — wide, spans 2 cols */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16 }}
            viewport={{ once: true }}
            className="md:col-span-2"
          >
            <Link
              href="/login?redirect=/survey-filler"
              className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 rounded-2xl border border-border bg-card p-6 hover:border-foreground/30 transition-colors duration-200"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="text-[11px] font-mono text-muted-foreground">04</span>
                  <TableProperties className="w-3.5 h-3.5 text-muted-foreground/40" />
                  <span className="text-[10px] font-mono tracking-wider text-blue-500 dark:text-blue-400">
                    BETA
                  </span>
                </div>
                <h3 className="text-base font-semibold mb-1.5">Survey Filler</h3>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
                  Paste a Google Form link. We generate diverse, realistic responses and submit them automatically — no manual work.
                </p>
              </div>
              <span className="shrink-0 px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground group-hover:border-foreground/40 transition-colors">
                Try it →
              </span>
            </Link>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
